/* eslint-disable */
import * as React from 'react';
import { useState, useEffect, useRef, FunctionComponent } from 'react';

// OpenLayers
import OlMap from 'ol/Map';
import OlView from 'ol/View';
import OlMapBrowserEvent from 'ol/MapBrowserEvent';
// import { MapOptions } from "ol/PluggableMap";
import { EventsKey } from 'ol/events';
import { unByKey } from 'ol/Observable';

import { MapOptions } from '@/@types/components/Map/Interfaces';

// Custom components
import MapContextProvider from '@/context/MapContext/MapContextProvider';
import { findChild, getDefinedOptions, getEvents } from '@/lib/olHelpers';
import { Controls } from './Controls';
import { Layers } from './Layers';
import { Overlays } from './Overlays';
import { Interactions } from './Interactions';

// Types
import { IMap } from '@/@types/components/Map';
import { InitOptionsType } from '@/context/MapContext/MapContext';

const LONGPRESS_MS = 500;
const LONGPRESS_MOVE_TOLERANCE = 10;

const Map: FunctionComponent<IMap> = (props: React.PropsWithChildren<IMap>) => {
  const mapElementRef = useRef<HTMLDivElement | null>(null);
  const { id, height, className, zoomToExtent, zoomToExtentPadding, onPointermove, onClick, onDblclick, onLongpress, onMoveend, view, children, mapId } = props;

  // we use useRef, as it provides us mutable object that persists between re-renders
  const map = useRef<OlMap | null>(null);
  const [initialized, setInitialized] = useState(false);
  const eventKeysRef = useRef<EventsKey[]>([]);

  // ref so a handler change mid-press doesn't cancel the press
  const onLongpressRef = useRef(onLongpress);
  onLongpressRef.current = onLongpress;
  const longpressFiredRef = useRef(false);

  const initOptionsRef = useRef<InitOptionsType>({
    controls: [],
    layers: [],
    overlays: [],
    interactions: [],
  });

  const options = {
    controls: undefined,
    pixelRatio: undefined,
    interactions: undefined,
    keyboardEventTarget: undefined,
    layers: undefined,
    maxTilesLoading: undefined,
    moveTolerance: undefined,
    overlays: undefined,
    target: undefined,
    view: new OlView({ center: [0, 0], zoom: 3 }),
  };

  const events = {
    change: undefined,
    'change:layerGroup': undefined,
    'change:size': undefined,
    'change:target': undefined,
    'change:view': undefined,
    click: undefined,
    dblclick: undefined,
    error: undefined,
    moveend: undefined,
    movestart: undefined,
    pointerdrag: undefined,
    pointermove: undefined,
    postcompose: undefined,
    postrender: undefined,
    precompose: undefined,
    propertychange: undefined,
    rendercomplete: undefined,
    singleclick: undefined,
  };

  const controlsComp = findChild(children, Controls);
  const layersComp = findChild(children, Layers);
  const overlaysComp = findChild(children, Overlays);
  const interactionsComp = findChild(children, Interactions);

  useEffect(() => {
    if (!map.current) {
      const allOptions = Object.assign(options, props);
      const mapOptions: MapOptions = getDefinedOptions(allOptions);
      !(mapOptions.view instanceof OlView) && (mapOptions.view = new OlView(options.view));

      if (controlsComp) {
        mapOptions.controls = initOptionsRef.current.controls;
      } else {
        // do nothing, we leave controls prop undefined -> OpenLayers will assign default controls
      }
      mapOptions.layers = initOptionsRef.current.layers;
      mapOptions.overlays = initOptionsRef.current.overlays;

      if (interactionsComp) {
        mapOptions.interactions = initOptionsRef.current.interactions;
      } else {
        // do nothing, we leave interactions prop undefined -> OpenLayers will assign defult interactions
      }

      const targetElement = mapElementRef.current;
      if (!targetElement) {
        return undefined;
      }

      const newMap = new OlMap(mapOptions);
      newMap.setTarget(targetElement);

      // regitster events
      const olEvents = getEvents(events, props);
      const evKeys: EventsKey[] = [];
      for (const eventName in olEvents) {
        // @ts-ignore
        const evKey = newMap.on(eventName, olEvents[eventName]);
        evKeys.push(evKey);
      }

      // assign newMap to our ref object
      map.current = newMap;
      setInitialized(true);
      eventKeysRef.current = evKeys;
    }

    return () => {
      if (map.current) {
        eventKeysRef.current.forEach((key) => unByKey(key));
        eventKeysRef.current = [];
        map.current.setTarget(undefined);
        map.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const padding = zoomToExtentPadding || [100, 100, 100, 100];
    if (map.current && zoomToExtent) {
      map.current.getView().fit(zoomToExtent, {
        size: map.current.getSize(),
        padding,
      });
    }
  }, [zoomToExtent, initialized, zoomToExtentPadding, props.initialized]);

  useEffect(() => {
    if (map.current) {
      map.current.getView().setCenter(view.center);
    }
  }, [view.center]);

  useEffect(() => {
    if (map.current) {
      map.current.getView().setZoom(view.zoom);
    }
  }, [view.zoom]);

  useEffect(() => {
    if (map.current) {
      const existingClickEvent = eventKeysRef.current.find((x) => x.type === 'click');
      if (existingClickEvent) {
        unByKey(existingClickEvent);
        eventKeysRef.current = eventKeysRef.current.filter((x) => x !== existingClickEvent);
      }

      if (onClick) {
        // @ts-ignore
        const newEvKey = map.current.on('click', (evt: OlMapBrowserEvent<any>) => {
          // OL emits click on release after a long press too
          if (longpressFiredRef.current) {
            longpressFiredRef.current = false;
            return;
          }
          onClick(evt);
        }) as EventsKey;
        eventKeysRef.current = [...eventKeysRef.current, newEvKey];
      }
    }
  }, [onClick]);

  useEffect(() => {
    const olMap = map.current;
    if (!olMap) {
      return undefined;
    }
    const viewport = olMap.getViewport();
    const touchPointers = new Set<number>();
    let downEvent: PointerEvent | null = null;
    let timer: number | undefined;
    let ring: HTMLDivElement | null = null;

    const cancel = () => {
      window.clearTimeout(timer);
      downEvent = null;
      ring?.remove();
      ring = null;
    };

    const handleDown = (e: PointerEvent) => {
      longpressFiredRef.current = false;
      if (e.pointerType !== 'touch') {
        return;
      }
      touchPointers.add(e.pointerId);
      cancel();
      // second finger = pinch; also ignore presses on controls and overlays (e.g. the GFI card)
      const target = e.target as Element | null;
      if (!onLongpressRef.current || touchPointers.size > 1 || target?.closest('.ol-overlaycontainer-stopevent')) {
        return;
      }

      downEvent = e;
      const [x, y] = olMap.getEventPixel(e);
      ring = document.createElement('div');
      ring.className = 'map-longpress-ring';
      ring.style.left = `${x}px`;
      ring.style.top = `${y}px`;
      viewport.appendChild(ring);

      timer = window.setTimeout(() => {
        if (!downEvent) {
          return;
        }
        longpressFiredRef.current = true;
        navigator.vibrate?.(20);
        onLongpressRef.current?.(new OlMapBrowserEvent('longpress', olMap, downEvent));
        cancel();
      }, LONGPRESS_MS);
    };

    const handleMove = (e: PointerEvent) => {
      if (downEvent && e.pointerId === downEvent.pointerId) {
        if (Math.hypot(e.clientX - downEvent.clientX, e.clientY - downEvent.clientY) > LONGPRESS_MOVE_TOLERANCE) {
          cancel();
        }
      }
    };

    const handleUp = (e: PointerEvent) => {
      touchPointers.delete(e.pointerId);
      cancel();
    };

    // Android opens the context menu on long press
    const handleContextMenu = (e: Event) => {
      if (downEvent || longpressFiredRef.current) {
        e.preventDefault();
      }
    };

    viewport.addEventListener('pointerdown', handleDown);
    viewport.addEventListener('pointermove', handleMove);
    viewport.addEventListener('pointerup', handleUp);
    viewport.addEventListener('pointercancel', handleUp);
    viewport.addEventListener('contextmenu', handleContextMenu);
    return () => {
      cancel();
      viewport.removeEventListener('pointerdown', handleDown);
      viewport.removeEventListener('pointermove', handleMove);
      viewport.removeEventListener('pointerup', handleUp);
      viewport.removeEventListener('pointercancel', handleUp);
      viewport.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [initialized]);

  useEffect(() => {
    if (map.current) {
      const existingDblclickEvent = eventKeysRef.current.find((x) => x.type === 'dblclick');
      if (existingDblclickEvent) {
        unByKey(existingDblclickEvent);
        eventKeysRef.current = eventKeysRef.current.filter((x) => x !== existingDblclickEvent);
      }

      if (onDblclick) {
        // @ts-ignore
        const newEvKey = map.current.on('dblclick', onDblclick) as EventsKey;
        eventKeysRef.current = [...eventKeysRef.current, newEvKey];
      }
    }
  }, [onDblclick]);

  return (
    <MapContextProvider mapId={mapId} map={map.current} initOptions={initOptionsRef.current}>
      <div className="map-container">
        {/* @ts-ignore TODO: type undefined is not assignable to type HTMLDivElement | null */}
        <div
          id={id || 'testmap'}
          className={`map openlayers-map ${className}`}
          // style={{ height: height || '100%' }}
          // @ts-ignore
          ref={mapElementRef}
        >
          {/* @ts-ignore TODO:Property 'props' does not exist on type '{} | ReactElement<any, string | JSXElementConstructor<any>>' */}
          {layersComp ? React.createElement(Layers, layersComp.props) : null}
          {/* @ts-ignore TODO:Property 'props' does not exist on type '{} | ReactElement<any, string | JSXElementConstructor<any>>' */}
          {controlsComp
            ? // @ts-ignore
              React.createElement(Controls, controlsComp.props)
            : null}
          {/* @ts-ignore TODO:Property 'props' does not exist on type '{} | ReactElement<any, string | JSXElementConstructor<any>>' */}
          {interactionsComp
            ? // @ts-ignore
              React.createElement(Interactions, interactionsComp.props)
            : null}
        </div>
        {/* @ts-ignore TODO:Property 'props' does not exist on type '{} | ReactElement<any, string | JSXElementConstructor<any>>' */}
        {overlaysComp
          ? // @ts-ignore
            React.createElement(Overlays, overlaysComp.props)
          : null}
      </div>
    </MapContextProvider>
  );
};

export default Map;
